import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Plus, RotateCcw } from 'lucide-react'
import { dashboardApi } from '@/api/dashboard'
import { learningApi } from '@/api/learning'
import { reviewApi, type ReviewCard, type ReviewMode, type SessionSummary } from '@/api/review'
import { settingsApi } from '@/api/settings'
import { EmptyState } from '@/components/EmptyState'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { PageHeader } from '@/components/PageHeader'
import { ExpandableFab } from '@/components/mobile/ExpandableFab'
import { FilterTriggerButton } from '@/components/mobile/FilterTriggerButton'
import { MobileBottomSheet, SheetOptionButton } from '@/components/mobile/MobileBottomSheet'
import { copy } from '@/lib/copy'
import { ExerciseActionHint } from '@/components/exercise/ExerciseActionHint'
import { FlashCard } from '@/components/review/FlashCard'
import { RatingButtons } from '@/components/review/RatingButtons'
import { SessionProgress } from '@/components/review/SessionProgress'
import { SessionSummary as SessionSummaryCard } from '@/components/review/SessionSummary'
import { MomentumComplete } from '@/components/review/MomentumComplete'
import { AddWordsToReviewDialog } from '@/components/review/AddWordsToReviewDialog'
import { ReviewModeSelector } from '@/components/review/ReviewModeSelector'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import {
  clearReviewSession,
  cardsMatchingSelectedMode,
  dedupeCardsById,
  filterCardsForDueQueue,
  isReviewSessionStale,
  loadReviewSession,
  mergePendingAgainCards,
  saveReviewSession,
} from '@/lib/reviewSessionStorage'
import { studyDurationMs } from '@/lib/studyDuration'
import { useActionGate } from '@/lib/useActionGate'

const REVIEW_MODE_OPTIONS: Array<{ value: ReviewMode; label: string }> = [
  { value: 'CLASSIC', label: copy.review.modeClassic },
  { value: 'REVERSE', label: copy.review.modeReverse },
  { value: 'FILL_BLANK', label: copy.review.modeFillBlank },
  { value: 'MULTIPLE_CHOICE', label: copy.review.modeMultipleChoice },
]

const createSessionId = (): string => crypto.randomUUID()

type ReviewPhase = 'loading' | 'empty' | 'reviewing' | 'summary'

export function ReviewSessionPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const fromContinue = searchParams.get('from') === 'continue'
  const learningSessionId = searchParams.get('learningSessionId')
  const learningStepIndex = searchParams.get('stepIndex')
  const inLearningFlow = Boolean(learningSessionId)
  const weakWordIds = useMemo(() => {
    const raw = searchParams.get('userWordIds')
    if (!raw) return undefined
    const ids = raw.split(',').map((id) => id.trim()).filter(Boolean)
    return ids.length > 0 ? ids : undefined
  }, [searchParams])

  const [reviewMode, setReviewMode] = useState<ReviewMode>('CLASSIC')
  const [reviewModeReady, setReviewModeReady] = useState(false)
  const settingsModeAppliedRef = useRef(false)

  const settingsQuery = useQuery({
    queryKey: ['settings'],
    queryFn: () => settingsApi.getSettings(),
    staleTime: 1000 * 60 * 5,
  })
  const nativeLanguage = settingsQuery.data?.nativeLanguage

  useEffect(() => {
    if (inLearningFlow || !settingsQuery.data || settingsModeAppliedRef.current) return
    settingsModeAppliedRef.current = true
    setReviewMode(settingsQuery.data.defaultReviewMode ?? 'CLASSIC')
    setReviewModeReady(true)
  }, [settingsQuery.data, inLearningFlow])

  const wantsRestoreRef = useRef(
    !inLearningFlow && (loadReviewSession()?.cards.length ?? 0) > 0,
  )
  const savedOnMount = useRef<ReturnType<typeof loadReviewSession>>(null)
  const initializedRef = useRef(false)

  const [sessionId, setSessionId] = useState(createSessionId)
  const [phase, setPhase] = useState<ReviewPhase>('loading')
  const [cards, setCards] = useState<ReviewCard[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isFlipped, setIsFlipped] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [correctCount, setCorrectCount] = useState(0)
  const [ratingsSubmitted, setRatingsSubmitted] = useState(0)
  const [sessionSummary, setSessionSummary] = useState<SessionSummary | null>(null)
  const [sessionStartedAt, setSessionStartedAt] = useState(Date.now)
  const [activeStudyMs, setActiveStudyMs] = useState(0)
  const [cardStartedAt, setCardStartedAt] = useState(Date.now())
  const activeStudyMsRef = useRef(0)
  const sessionStartedAtRef = useRef(sessionStartedAt)
  const studyPausedRef = useRef(false)
  const [userInput, setUserInput] = useState('')
  const [selectedMcOption, setSelectedMcOption] = useState<string | null>(null)
  const [fillBlankRevealed, setFillBlankRevealed] = useState(false)
  const [showAddWords, setShowAddWords] = useState(false)
  const [modeSheetOpen, setModeSheetOpen] = useState(false)
  const [disableFlipTransition, setDisableFlipTransition] = useState(false)
  const [pendingAgainCardIds, setPendingAgainCardIds] = useState<string[]>([])
  const [bootstrapNonce, setBootstrapNonce] = useState(0)
  const { hint: actionHint, clearHint, showHint } = useActionGate()

  const learningSessionQuery = useQuery({
    queryKey: ['learningSession', learningSessionId],
    queryFn: () => learningApi.getSession(learningSessionId!),
    enabled: !!learningSessionId,
  })

  const exploreWordIds = useMemo(() => {
    if (!learningSessionQuery.data || learningStepIndex == null) return undefined
    const step = learningSessionQuery.data.steps[Number(learningStepIndex)]
    const ids = step?.config?.userWordIds
    if (Array.isArray(ids) && ids.length > 0) {
      return ids.map(String)
    }
    return undefined
  }, [learningSessionQuery.data, learningStepIndex])

  const reviewLimit = useMemo(() => {
    if (!learningSessionQuery.data || learningStepIndex == null) return 20
    const step = learningSessionQuery.data.steps[Number(learningStepIndex)]
    const limit = step?.config?.limit
    return typeof limit === 'number' ? limit : 20
  }, [learningSessionQuery.data, learningStepIndex])

  const targetWordIds = weakWordIds ?? exploreWordIds
  const activeReviewMode: ReviewMode = inLearningFlow ? 'CLASSIC' : reviewMode

  const dueCardsQuery = useQuery({
    queryKey: ['reviewDueCards', learningSessionId, targetWordIds?.join(','), reviewLimit, activeReviewMode],
    queryFn: () =>
      reviewApi.getDueCards(reviewLimit, activeReviewMode, targetWordIds),
    staleTime: 0,
    retry: 1,
    enabled:
      (!learningSessionId || !!learningSessionQuery.data) &&
      (inLearningFlow || reviewModeReady),
  })

  const dashboardQuery = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => dashboardApi.getDashboard(),
    enabled: fromContinue && !inLearningFlow,
    staleTime: 1000 * 60,
  })

  // Bootstrap from API when no saved session, or merge pending-again on restore
  useEffect(() => {
    if (dueCardsQuery.isLoading || settingsQuery.isLoading) {
      setPhase('loading')
      return
    }

    if (dueCardsQuery.isError) {
      setCards([])
      setPhase('empty')
      initializedRef.current = true
      return
    }

    if (!dueCardsQuery.data) return

    const dueCards = cardsMatchingSelectedMode(dueCardsQuery.data, activeReviewMode)
    const saved = loadReviewSession()
    savedOnMount.current = saved

    if (saved && isReviewSessionStale(saved, dueCards, nativeLanguage)) {
      clearReviewSession()
      savedOnMount.current = null
      wantsRestoreRef.current = false
    }

    if (wantsRestoreRef.current && !initializedRef.current && savedOnMount.current) {
      const savedSession = savedOnMount.current
      const validCards = cardsMatchingSelectedMode(
        filterCardsForDueQueue(savedSession.cards, dueCards),
        activeReviewMode,
      )

      if (validCards.length === 0) {
        clearReviewSession()
        setSessionId(createSessionId())
        setCards(dedupeCardsById(dueCards))
        setCurrentIndex(0)
        setCorrectCount(0)
        setRatingsSubmitted(0)
        setPendingAgainCardIds([])
        setActiveStudyMs(0)
        activeStudyMsRef.current = 0
        const now = Date.now()
        setSessionStartedAt(now)
        sessionStartedAtRef.current = now
        studyPausedRef.current = false
        setCardStartedAt(now)
        setPhase(dueCards.length > 0 ? 'reviewing' : 'empty')
        wantsRestoreRef.current = false
        initializedRef.current = true
        return
      }

      const baseIndex = Math.min(savedSession.currentIndex, validCards.length - 1)
      const merged = mergePendingAgainCards(
        validCards,
        baseIndex,
        savedSession.pendingAgainCardIds ?? savedSession.pendingAgainWordIds ?? [],
        dueCards,
      )
      const restoredActiveMs = savedSession.activeStudyMs ?? 0
      const now = Date.now()
      setSessionId(savedSession.sessionId)
      setCards(merged.cards)
      setCurrentIndex(baseIndex)
      setCorrectCount(savedSession.correctCount)
      setRatingsSubmitted(savedSession.ratingsSubmitted)
      // Do not resume wall-clock from the original start — idle while away is not study time.
      setActiveStudyMs(restoredActiveMs)
      activeStudyMsRef.current = restoredActiveMs
      setSessionStartedAt(now)
      sessionStartedAtRef.current = now
      studyPausedRef.current = false
      setCardStartedAt(now)
      setPendingAgainCardIds(merged.pendingAgainCardIds)
      setPhase(merged.cards.length > 0 ? 'reviewing' : 'empty')
      wantsRestoreRef.current = false
      initializedRef.current = true
      return
    }

    if (initializedRef.current) return

    const now = Date.now()
    setSessionId(createSessionId())
    setCards(dedupeCardsById(dueCards))
    setCurrentIndex(0)
    setIsFlipped(false)
    setIsSubmitting(false)
    setCorrectCount(0)
    setRatingsSubmitted(0)
    setPendingAgainCardIds([])
    setUserInput('')
    setSelectedMcOption(null)
    setFillBlankRevealed(false)
    setSessionSummary(null)
    setActiveStudyMs(0)
    activeStudyMsRef.current = 0
    setSessionStartedAt(now)
    sessionStartedAtRef.current = now
    studyPausedRef.current = false
    setCardStartedAt(now)
    setPhase(dueCards.length > 0 ? 'reviewing' : 'empty')
    initializedRef.current = true
  }, [
    activeReviewMode,
    bootstrapNonce,
    dueCardsQuery.data,
    dueCardsQuery.isError,
    dueCardsQuery.isLoading,
    nativeLanguage,
    settingsQuery.isLoading,
  ])

  // Persist in-progress session
  useEffect(() => {
    if (phase !== 'reviewing' || cards.length === 0) return
    saveReviewSession({
      sessionId,
      cards,
      currentIndex,
      correctCount,
      ratingsSubmitted,
      sessionStartedAt,
      activeStudyMs: studyDurationMs(activeStudyMs, sessionStartedAt, {
        paused: studyPausedRef.current,
      }),
      nativeLanguage,
      pendingAgainCardIds,
    })
  }, [
    activeStudyMs,
    cards,
    correctCount,
    currentIndex,
    nativeLanguage,
    pendingAgainCardIds,
    phase,
    ratingsSubmitted,
    sessionId,
    sessionStartedAt,
  ])

  // Pause study clock while the tab is hidden so overnight idle is not credited.
  useEffect(() => {
    if (phase !== 'reviewing') return

    const onVisibilityChange = () => {
      if (document.hidden) {
        if (studyPausedRef.current) return
        const now = Date.now()
        const nextActive =
          activeStudyMsRef.current + Math.max(0, now - sessionStartedAtRef.current)
        activeStudyMsRef.current = nextActive
        setActiveStudyMs(nextActive)
        studyPausedRef.current = true
        return
      }
      if (!studyPausedRef.current) return
      const now = Date.now()
      sessionStartedAtRef.current = now
      setSessionStartedAt(now)
      setCardStartedAt(now)
      studyPausedRef.current = false
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [phase])

  const currentCard = cards[currentIndex]
  const totalCards = cards.length
  const completedCards = useMemo(
    () => Math.min(currentIndex, totalCards),
    [currentIndex, totalCards],
  )

  const existingWordIds = useMemo(
    () => new Set(cards.map((c) => c.wordId)),
    [cards],
  )

  const handleAddWords = useCallback((newCards: ReviewCard[], mode: 'append' | 'replace' = 'append') => {
    const matching = cardsMatchingSelectedMode(newCards, activeReviewMode)
    if (matching.length === 0) return

    if (mode === 'replace') {
      const now = Date.now()
      setCards(dedupeCardsById(matching))
      setCurrentIndex(0)
      setIsFlipped(false)
      setPendingAgainCardIds([])
      setCorrectCount(0)
      setRatingsSubmitted(0)
      setUserInput('')
      setSelectedMcOption(null)
      setFillBlankRevealed(false)
      setActiveStudyMs(0)
      activeStudyMsRef.current = 0
      setSessionStartedAt(now)
      sessionStartedAtRef.current = now
      studyPausedRef.current = false
      setCardStartedAt(now)
      setPhase('reviewing')
      return
    }

    setCards((prev) => dedupeCardsById([...prev, ...matching]))
    if (phase === 'empty') {
      const now = Date.now()
      setCurrentIndex(0)
      setIsFlipped(false)
      setActiveStudyMs(0)
      activeStudyMsRef.current = 0
      setSessionStartedAt(now)
      sessionStartedAtRef.current = now
      studyPausedRef.current = false
      setCardStartedAt(now)
      setPhase('reviewing')
    }
  }, [activeReviewMode, phase])

  const handleFlip = useCallback(() => {
    if (!currentCard || currentCard.mode === 'MULTIPLE_CHOICE') return
    setIsFlipped((value) => !value)
  }, [currentCard])

  const handleSelectMcOption = useCallback((optionId: string) => {
    if (!currentCard || currentCard.mode !== 'MULTIPLE_CHOICE' || selectedMcOption) return
    clearHint()
    setSelectedMcOption(optionId)
  }, [clearHint, currentCard, selectedMcOption])

  const handleCheckFillBlank = useCallback(() => {
    if (!currentCard || currentCard.mode !== 'FILL_BLANK' || fillBlankRevealed || !userInput.trim()) return
    clearHint()
    setFillBlankRevealed(true)
  }, [clearHint, currentCard, fillBlankRevealed, userInput])

  const ratingsBlocked = useMemo(() => {
    if (!currentCard || isSubmitting) return true
    if (currentCard.mode === 'MULTIPLE_CHOICE') {
      return !selectedMcOption
    }
    if (currentCard.mode === 'FILL_BLANK') {
      return !fillBlankRevealed
    }
    return false
  }, [currentCard, fillBlankRevealed, isSubmitting, selectedMcOption])

  const handleRatingBlockedAttempt = useCallback(() => {
    if (!currentCard) return
    if (currentCard.mode === 'FILL_BLANK') {
      showHint(
        userInput.trim()
          ? copy.review.fillBlankNeedCheck
          : copy.review.fillBlankNeedAnswer,
      )
      return
    }
    if (currentCard.mode === 'MULTIPLE_CHOICE') {
      showHint(copy.review.multipleChoiceNeedOption)
    }
  }, [currentCard, showHint, userInput])

  useEffect(() => {
    clearHint()
  }, [currentCard?.id, clearHint])

  const handleRate = useCallback(async (rating: 0 | 1 | 2 | 3) => {
    if (!currentCard || isSubmitting) return

    setIsSubmitting(true)
    const nextCorrectCount = correctCount + (rating >= 2 ? 1 : 0)
    const nextRatingsSubmitted = ratingsSubmitted + 1

    try {
      await reviewApi.submitReview({
        ...(currentCard.unitType === 'GRAMMAR'
          ? { episodeId: currentCard.id }
          : currentCard.unitType && currentCard.unitType !== 'WORD'
            ? { userPhraseId: currentCard.id }
            : { userWordId: currentCard.id }),
        wordId: currentCard.wordId,
        rating,
        answerTimeMs: Date.now() - cardStartedAt,
        sessionId,
      })

      setCorrectCount(nextCorrectCount)
      setRatingsSubmitted(nextRatingsSubmitted)

      if (rating === 0) {
        setPendingAgainCardIds((prev) =>
          prev.includes(currentCard.id) ? prev : [...prev, currentCard.id],
        )
      } else if (rating >= 2) {
        setPendingAgainCardIds((prev) => prev.filter((id) => id !== currentCard.id))
      }

      const isLastInQueue = currentIndex >= cards.length - 1

      if (isLastInQueue) {
        const durationMs = studyDurationMs(
          activeStudyMsRef.current,
          sessionStartedAtRef.current,
          { paused: studyPausedRef.current },
        )
        const summary = await reviewApi.completeSession({
          sessionId,
          cardsReviewed: nextRatingsSubmitted,
          correctCount: nextCorrectCount,
          durationMs,
        })

        clearReviewSession()

        if (learningSessionId && learningStepIndex != null) {
          await learningApi.completeStep(learningSessionId, Number(learningStepIndex), {
            cardsReviewed: nextRatingsSubmitted,
            correctCount: nextCorrectCount,
            durationMs,
          })
          navigate(`/learn/${learningSessionId}`)
          return
        }

        setSessionSummary(summary)
        setPhase('summary')
        return
      }

      setDisableFlipTransition(true)
      setIsFlipped(false)
      setCurrentIndex((value) => value + 1)
      setUserInput('')
      setSelectedMcOption(null)
      setFillBlankRevealed(false)
      setCardStartedAt(Date.now())
    } finally {
      setIsSubmitting(false)
    }
  }, [
    cardStartedAt,
    cards.length,
    correctCount,
    currentCard,
    currentIndex,
    isSubmitting,
    learningSessionId,
    learningStepIndex,
    navigate,
    ratingsSubmitted,
    sessionId,
  ])

  useEffect(() => {
    if (phase !== 'empty' || !inLearningFlow || !learningSessionId || learningStepIndex == null) return
    if (!initializedRef.current) return

    let cancelled = false
    void (async () => {
      await learningApi.completeStep(learningSessionId, Number(learningStepIndex), {
        cardsReviewed: 0,
        correctCount: 0,
        durationMs: 0,
      })
      if (!cancelled) navigate(`/learn/${learningSessionId}`)
    })()

    return () => {
      cancelled = true
    }
  }, [phase, inLearningFlow, learningSessionId, learningStepIndex, navigate])

  const handleReviewModeChange = useCallback((mode: ReviewMode) => {
    if (inLearningFlow) return
    setReviewMode(mode)
    clearReviewSession()
    initializedRef.current = false
    wantsRestoreRef.current = false
    savedOnMount.current = null
    setSessionId(createSessionId())
    setPendingAgainCardIds([])
    setCurrentIndex(0)
    setCards([])
    setCorrectCount(0)
    setRatingsSubmitted(0)
    setSessionSummary(null)
    setSelectedMcOption(null)
    setFillBlankRevealed(false)
    setPhase('loading')
    setBootstrapNonce((n) => n + 1)
    void dueCardsQuery.refetch()
  }, [dueCardsQuery])

  const handleRestart = useCallback(() => {
    clearReviewSession()
    initializedRef.current = false
    wantsRestoreRef.current = false
    savedOnMount.current = null
    setSessionId(createSessionId())
    setPendingAgainCardIds([])
    setCurrentIndex(0)
    setCards([])
    setCorrectCount(0)
    setRatingsSubmitted(0)
    setSessionSummary(null)
    setSelectedMcOption(null)
    setFillBlankRevealed(false)
    setPhase('loading')
    setBootstrapNonce((n) => n + 1)
    void dueCardsQuery.refetch()
  }, [dueCardsQuery])

  useEffect(() => {
    if (disableFlipTransition) {
      setDisableFlipTransition(false)
    }
  }, [currentIndex, disableFlipTransition])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (phase !== 'reviewing' || !currentCard || isSubmitting) return

      const target = event.target
      if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) return

      if (event.code === 'Space') {
        event.preventDefault()
        handleFlip()
        return
      }

      if (event.key === '1') handleRate(0)
      else if (event.key === '2') handleRate(1)
      else if (event.key === '3') handleRate(2)
      else if (event.key === '4') handleRate(3)
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [currentCard, handleFlip, handleRate, isSubmitting, phase])

  const headerTitle = fromContinue || inLearningFlow ? copy.pages.continueLearning : copy.pages.refresh
  const headerDescription =
    fromContinue || inLearningFlow
      ? phase === 'summary'
        ? 'Session complete'
        : 'Refresh your memory — one step at a time.'
      : phase === 'summary'
        ? 'Session results'
        : phase === 'reviewing'
          ? `${totalCards} cards in queue · ${ratingsSubmitted} reviewed`
          : 'Strengthen recall with spaced repetition.'
  const activeModeLabel =
    REVIEW_MODE_OPTIONS.find((m) => m.value === reviewMode)?.label ?? copy.review.modeClassic
  const showAddWordsFab = phase === 'reviewing' && !inLearningFlow

  return (
    <div className="min-w-0 overflow-x-hidden">
      {/* Desktop header */}
      <div className="hidden lg:block">
        <PageHeader
          title={headerTitle}
          description={headerDescription}
          action={
            showAddWordsFab ? (
              <Button className="min-h-12 w-auto gap-2" onClick={() => setShowAddWords(true)}>
                <Plus className="h-4 w-4" />
                Add words
              </Button>
            ) : undefined
          }
        />
      </div>

      {!inLearningFlow && phase !== 'summary' ? (
        <div className="mb-3 lg:mb-6">
          <div className="lg:hidden">
            <FilterTriggerButton
              label={activeModeLabel}
              onClick={() => setModeSheetOpen(true)}
              testId="review-mode-filter"
            />
          </div>
          <div className="hidden lg:block">
            <ReviewModeSelector
              value={reviewMode}
              onChange={handleReviewModeChange}
              disabled={phase === 'loading' || isSubmitting}
            />
          </div>
        </div>
      ) : null}

      {phase === 'loading' && (
        <div className="flex h-64 items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {phase === 'empty' && inLearningFlow && (
        <div className="flex h-64 items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {phase === 'empty' && fromContinue && !inLearningFlow && (
        <MomentumComplete
          tomorrowDueCount={dashboardQuery.data?.continueLearning.tomorrowDueCountStub ?? 0}
          caughtUp
        />
      )}

      {phase === 'empty' && !fromContinue && !inLearningFlow && (
        <EmptyState
          title="No words due for review today!"
          description="Pick words manually to start a custom review session, or come back later."
          action={
            <div className="flex flex-wrap justify-center gap-2">
              <Button className="w-auto gap-2" onClick={() => setShowAddWords(true)}>
                <Plus className="h-4 w-4" />
                Pick words to review
              </Button>
              {dueCardsQuery.isError && (
                <Button
                  className="w-auto gap-2 bg-secondary text-secondary-foreground hover:bg-secondary/80"
                  onClick={() => dueCardsQuery.refetch()}
                >
                  <RotateCcw className="h-4 w-4" />
                  Try again
                </Button>
              )}
            </div>
          }
        />
      )}

      {phase === 'reviewing' && currentCard && (
        <div className="mx-auto min-w-0 max-w-full space-y-4 sm:max-w-4xl sm:space-y-6">
          <SessionProgress done={completedCards} total={totalCards} />

          <FlashCard
            key={currentCard.id}
            card={currentCard}
            isFlipped={isFlipped}
            onFlip={handleFlip}
            userInput={userInput}
            onInputChange={(value) => {
              setUserInput(value)
              if (value.trim()) clearHint()
            }}
            selectedOptionId={selectedMcOption}
            onSelectOption={handleSelectMcOption}
            fillBlankRevealed={fillBlankRevealed}
            onCheckFillBlank={handleCheckFillBlank}
            onCheckFillBlankBlocked={() => showHint(copy.review.fillBlankNeedAnswer)}
            disableFlipTransition={disableFlipTransition}
          />

          <ExerciseActionHint message={actionHint} />

          <RatingButtons
            onRate={handleRate}
            blocked={ratingsBlocked}
            onBlockedAttempt={handleRatingBlockedAttempt}
          />

          <Card className="hidden p-4 text-sm text-muted-foreground md:block">
            <p><strong>Shortcuts:</strong> Space to flip, 1 = Again, 2 = Hard, 3 = Good, 4 = Easy.</p>
          </Card>
        </div>
      )}

      {phase === 'summary' && sessionSummary && fromContinue && !inLearningFlow && (
        <MomentumComplete
          summary={sessionSummary}
          tomorrowDueCount={dashboardQuery.data?.continueLearning.tomorrowDueCountStub ?? 0}
        />
      )}

      {phase === 'summary' && sessionSummary && !fromContinue && !inLearningFlow && (
        <SessionSummaryCard summary={sessionSummary} onRestart={handleRestart} />
      )}

      <AddWordsToReviewDialog
        open={showAddWords}
        onClose={() => setShowAddWords(false)}
        onAdd={handleAddWords}
        existingWordIds={existingWordIds}
        hasExistingCards={cards.length > 0}
        reviewMode={activeReviewMode}
      />

      {showAddWordsFab ? (
        <ExpandableFab
          label="Add words"
          onAction={() => setShowAddWords(true)}
          testId="review-add-fab"
        />
      ) : null}

      <MobileBottomSheet
        open={modeSheetOpen}
        title="Review mode"
        onClose={() => setModeSheetOpen(false)}
        testId="review-mode-sheet"
      >
        {REVIEW_MODE_OPTIONS.map((mode) => (
          <SheetOptionButton
            key={mode.value}
            active={reviewMode === mode.value}
            label={mode.label}
            testId={`review-mode-${mode.value.toLowerCase()}`}
            onClick={() => {
              handleReviewModeChange(mode.value)
              setModeSheetOpen(false)
            }}
          />
        ))}
      </MobileBottomSheet>
    </div>
  )
}
